/**
 * Route guard. Redirects anonymous users to sign in and never flashes protected
 * data. The redirect target depends on the auth mode:
 *   - simple   → our own `/login` form (client-side navigation).
 *   - auth0     → the Auth0 redirect (external, via window.location).
 *   - skyagent  → the SkyAgent handoff redirect (external, via window.location).
 *   - disabled  → never triggers (the dev user is always "authenticated").
 */
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAuthMode, redirectToLogin } from "@/lib/auth";
import { useAuth } from "@/lib/auth/useAuth";

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-fg-faint">
      {children}
    </div>
  );
}

/** Auth modes that use an external redirect (top-level navigation). */
const EXTERNAL_REDIRECT_MODES: ReadonlySet<string> = new Set(["auth0", "skyagent"]);

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const from = location.pathname + location.search;

  const mode = getAuthMode();
  const isExternal = EXTERNAL_REDIRECT_MODES.has(mode);

  // External redirect modes — run as a side-effect, not in render.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && isExternal) {
      redirectToLogin(from);
    }
  }, [isLoading, isAuthenticated, from, isExternal]);

  if (isLoading) return <Centered>Checking your session…</Centered>;

  if (!isAuthenticated) {
    if (isExternal) {
      return <Centered>Redirecting to sign in…</Centered>;
    }
    // Simple (and any non-external) mode → client-side redirect to our /login form.
    return <Navigate to={`/login?returnTo=${encodeURIComponent(from)}`} replace />;
  }

  return <>{children}</>;
}
